import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, MapPin } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['praxis', 'leistungen', 'team', 'sprechzeiten', 'kontakt'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo/Brand */}
          <div className="flex-shrink-0 flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_frauenarzt-gronau/artifacts/vdzl8u22_Logo.jpeg" 
              alt="Frauenarztpraxis Dr. med. Christina Weiß Logo"
              className="h-12 w-auto mr-3"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">
                Frauenarztpraxis
              </h1>
              <p className="text-sm text-red-600 font-medium">
                Dr. med. Christina Weiß
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <button
              onClick={() => scrollToSection('praxis')}
              className={`transition-colors duration-200 ${
                activeSection === 'praxis' 
                  ? 'text-red-600 font-medium' 
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Praxis
            </button>
            <button
              onClick={() => scrollToSection('leistungen')}
              className={`transition-colors duration-200 ${
                activeSection === 'leistungen' 
                  ? 'text-red-600 font-medium' 
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Leistungen
            </button>
            <button
              onClick={() => scrollToSection('team')}
              className={`transition-colors duration-200 ${
                activeSection === 'team' 
                  ? 'text-red-600 font-medium' 
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Team
            </button>
            <button
              onClick={() => scrollToSection('sprechzeiten')}
              className={`transition-colors duration-200 ${
                activeSection === 'sprechzeiten' 
                  ? 'text-red-600 font-medium' 
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Sprechzeiten
            </button>
            <button
              onClick={() => scrollToSection('kontakt')}
              className={`transition-colors duration-200 ${
                activeSection === 'kontakt' 
                  ? 'text-red-600 font-medium' 
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Kontakt
            </button>
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center text-sm text-gray-600">
              <Phone className="h-4 w-4 mr-1" />
              <a 
                href="tel:05182-1212"
                className="text-red-600 hover:text-red-700 underline"
              >
                05182-1212
              </a>
            </div>
            <Button 
              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
              onClick={() => window.open('https://app.arzt-direkt.de/frauenarzt-praxis-gronau/booking', '_blank')}
            >
              Termin buchen
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-red-600"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="py-4 space-y-3">
              <button
                onClick={() => scrollToSection('praxis')}
                className={`block w-full text-left py-2 transition-colors duration-200 ${
                  activeSection === 'praxis' 
                    ? 'text-red-600 font-medium' 
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                Praxis
              </button>
              <button
                onClick={() => scrollToSection('leistungen')}
                className={`block w-full text-left py-2 transition-colors duration-200 ${
                  activeSection === 'leistungen' 
                    ? 'text-red-600 font-medium' 
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                Leistungen
              </button>
              <button
                onClick={() => scrollToSection('team')}
                className={`block w-full text-left py-2 transition-colors duration-200 ${
                  activeSection === 'team' 
                    ? 'text-red-600 font-medium' 
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                Team
              </button>
              <button
                onClick={() => scrollToSection('sprechzeiten')}
                className={`block w-full text-left py-2 transition-colors duration-200 ${
                  activeSection === 'sprechzeiten' 
                    ? 'text-red-600 font-medium' 
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                Sprechzeiten
              </button>
              <button
                onClick={() => scrollToSection('kontakt')}
                className={`block w-full text-left py-2 transition-colors duration-200 ${
                  activeSection === 'kontakt' 
                    ? 'text-red-600 font-medium' 
                    : 'text-gray-700 hover:text-red-600'
                }`}
              >
                Kontakt
              </button>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <Phone className="h-4 w-4 mr-2" />
                  <a 
                    href="tel:05182-1212"
                    className="text-red-600 hover:text-red-700 underline"
                  >
                    05182-1212
                  </a>
                </div>
                <Button 
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                  onClick={() => window.open('https://app.arzt-direkt.de/frauenarzt-praxis-gronau/booking', '_blank')}
                >
                  Termin buchen
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;